import React, { useState, useEffect, useRef, useState as useStateHook } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { useTheme } from '../contexts/ThemeContext';
import {
  Users,
  UserPlus,
  Shield,
  Edit,
  Trash2,
  Eye,
  Settings,
  CheckCircle,
  XCircle,
  Clock,
  Mail,
  FileText,
  MessageCircle,
  Lock,
  GitBranch,
  Loader2,
  Bell,
  Database,
  Globe,
  Save,
  RefreshCw
} from 'lucide-react';
import UserManagement from './UserManagement';
import UserManagementModal from './UserManagementModal';
import EmailIntegrationSettings from './EmailIntegrationSettings';
import SharePointIntegrationSettings from './SharePointIntegrationSettings';
import ProductPermissionsSettings from './ProductPermissionsSettings';
import MessengerPermissionsSettings from './MessengerPermissionsSettings';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';


const API = "/api";

const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    pendingUsers: 0,
    adminUsers: 0
  });
  const [loading, setLoading] = useState(false);
  const [tabLoading, setTabLoading] = useState(false);
  const [showUserManagementModal, setShowUserManagementModal] = useState(false);

  // System Settings State
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settings, setSettings] = useState({
    allowUserRegistration: false,
    requireEmailVerification: true,
    enableNotifications: true,
    defaultUserRole: 'read_only',
    sessionTimeout: 24,
    maxFileSize: 10,
    enableAuditLog: true
  });
  const [emailConfig, setEmailConfig] = useState(null);
  const [diskSpace, setDiskSpace] = useState(null);

  const { theme, setTheme } = useTheme();
  const { toast } = useToast();
  const abortControllerRef = useRef(null);

  // Fetch email config and disk space on component mount
  useEffect(() => {
    fetchEmailConfig();
    fetchDiskSpace().then(data => setDiskSpace(data));
  }, []);

  // System Settings Functions
  const handleSettingChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSaveSettings = async () => {
    setSettingsLoading(true);
    try {
      // In a real app, this would save to backend
      await new Promise(resolve => setTimeout(resolve, 1000));

      toast({
        title: "Success",
        description: "Settings saved successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save settings",
        variant: "destructive",
      });
    } finally {
      setSettingsLoading(false);
    }
  };

  // Fetch email configuration for status checking
  const fetchEmailConfig = async () => {
    try {
      const response = await axios.get(`${API}/admin/email-config`);
      setEmailConfig(response.data);
    } catch (error) {
      console.error('Error fetching email config:', error);
      // Set default empty config so it doesn't break the UI
      setEmailConfig({
        configured: false,
        type: "Microsoft Graph API",
        graph_client_id: '',
        graph_tenant_id: '',
        sender_email: '',
        sender_name: '',
        credentials_set: false,
        token_obtained: false,
        error: "Failed to fetch configuration"
      });
    }
  };

  // Get disk space information
  const fetchDiskSpace = async () => {
    try {
      const response = await axios.get(`${API}/admin/disk-space`);
      return response.data;
    } catch (error) {
      console.error('Error fetching disk space:', error);
      return null;
    }
  };

  const fetchWithRetry = async (requestFn, maxRetries = 3) => {
    let lastError;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await requestFn();
      } catch (error) {
        lastError = error;
        
        // Don't retry on cancellation
        if (error.name === 'CanceledError') {
          throw error;
        }
        
        // Don't retry on last attempt
        if (attempt === maxRetries) {
          break;
        }
        
        // Wait before retry (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
      }
    }
    
    throw lastError;
  };

  const fetchUsers = async () => {
    // Cancel previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    setTabLoading(true);
    
    try {
      const response = await fetchWithRetry(async () => {
        return await axios.get(`${API}/users`, {
          signal: abortControllerRef.current.signal
        });
      });
      
      const usersData = response.data;
      setUsers(usersData);
      
      // Calculate stats
      setStats({
        totalUsers: usersData.length,
        activeUsers: usersData.filter(u => u.status === 'active').length,
        pendingUsers: usersData.filter(u => u.status === 'pending').length,
        adminUsers: usersData.filter(u => u.role === 'admin').length
      });
    } catch (error) {
      if (error.name !== 'CanceledError') {
        console.error('Error fetching users:', error);
        toast({
          title: "Error",
          description: "Failed to fetch users. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setTabLoading(false);
      abortControllerRef.current = null;
    }
  };

  useEffect(() => {
    // Only fetch users if we're on the users tab
    if (activeTab === 'users') {
      fetchUsers();
    }
    
    // Cleanup function to cancel pending requests
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [activeTab]);

  const roleConfig = {
    admin: { label: 'Full Control', color: 'bg-red-100 text-red-800 border-red-200', icon: Shield },
    contributor: { label: 'Contributor', color: 'bg-blue-100 text-blue-800 border-blue-200', icon: Edit },
    read_only: { label: 'Read Only', color: 'bg-gray-100 text-gray-800 border-gray-200', icon: Eye }
  };

  const statusConfig = {
    active: { label: 'Active', color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle },
    inactive: { label: 'Inactive', color: 'bg-gray-100 text-gray-800 border-gray-200', icon: XCircle },
    pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Clock }
  };

  return (
    <div className="space-y-6">
      {/* Admin Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm bg-gradient-to-br from-blue-50 to-blue-100">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-blue-700">
              Total Users
            </CardTitle>
            <Users className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-900">{stats.totalUsers}</div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-gradient-to-br from-green-50 to-green-100">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-green-700">
              Active Users
            </CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-900">{stats.activeUsers}</div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-gradient-to-br from-yellow-50 to-yellow-100">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-yellow-700">
              Pending Users
            </CardTitle>
            <Clock className="h-4 w-4 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-900">{stats.pendingUsers}</div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-gradient-to-br from-red-50 to-red-100">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-red-700">
              Admin Users
            </CardTitle>
            <Shield className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-900">{stats.adminUsers}</div>
          </CardContent>
        </Card>
      </div>

      {/* Admin Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-9 bg-slate-100 rounded-lg p-1">
          <TabsTrigger value="users" className="flex items-center space-x-2">
            <Users className="w-4 h-4" />
            <span>Users</span>
          </TabsTrigger>
          <TabsTrigger value="general" className="flex items-center space-x-2">
            <Settings className="w-4 h-4" />
            <span>General</span>
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center space-x-2">
            <Shield className="w-4 h-4" />
            <span>Security</span>
          </TabsTrigger>
          <TabsTrigger value="system" className="flex items-center space-x-2">
            <Database className="w-4 h-4" />
            <span>System</span>
          </TabsTrigger>
          <TabsTrigger value="email" className="flex items-center space-x-2">
            <Mail className="w-4 h-4" />
            <span>Email</span>
          </TabsTrigger>
          <TabsTrigger value="sharepoint" className="flex items-center space-x-2">
            <FileText className="w-4 h-4" />
            <span>SharePoint</span>
          </TabsTrigger>
          <TabsTrigger value="products" className="flex items-center space-x-2">
            <Shield className="w-4 h-4" />
            <span>Products</span>
          </TabsTrigger>
          <TabsTrigger value="messenger" className="flex items-center space-x-2">
            <MessageCircle className="w-4 h-4" />
            <span>Messenger</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <UserManagement 
            users={users}
            onRefresh={fetchUsers}
            loading={tabLoading}
            roleConfig={roleConfig}
            statusConfig={statusConfig}
            setActiveTab={setActiveTab}
          />
        </TabsContent>

        <TabsContent value="general">
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Settings className="w-5 h-5" />
                    <span>General Settings</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Allow User Registration</Label>
                      <p className="text-xs text-slate-500">
                        Let users create their own accounts
                      </p>
                    </div>
                    <Switch
                      checked={settings.allowUserRegistration}
                      onCheckedChange={(checked) => handleSettingChange('allowUserRegistration', checked)}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Require Email Verification</Label>
                      <p className="text-xs text-slate-500">
                        Users must verify email before activation
                      </p>
                    </div>
                    <Switch
                      checked={settings.requireEmailVerification}
                      onCheckedChange={(checked) => handleSettingChange('requireEmailVerification', checked)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="sessionTimeout">Session Timeout (hours)</Label>
                    <Input
                      id="sessionTimeout"
                      type="number"
                      min="1"
                      max="168"
                      value={settings.sessionTimeout}
                      onChange={(e) => handleSettingChange('sessionTimeout', parseInt(e.target.value))}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Bell className="w-5 h-5" />
                    <span>Notifications</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Enable Notifications</Label>
                      <p className="text-xs text-slate-500">
                        Send system notifications to users
                      </p>
                    </div>
                    <Switch
                      checked={settings.enableNotifications}
                      onCheckedChange={(checked) => handleSettingChange('enableNotifications', checked)}
                    />
                  </div>

                  <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <h4 className="font-medium text-slate-700 mb-2">Notification Types</h4>
                    <div className="space-y-2 text-sm text-slate-600">
                      <div>• Task assignments and updates</div>
                      <div>• Product stage changes</div>
                      <div>• User account changes</div>
                      <div>• System maintenance alerts</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Globe className="w-5 h-5" />
                    <span>Appearance</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="theme">Theme</Label>
                    <select
                      id="theme"
                      value={theme}
                      onChange={(e) => setTheme(e.target.value)}
                      className="w-full p-2 border border-slate-300 rounded-md text-sm"
                    >
                      <option value="light">Light</option>
                      <option value="dark">Dark</option>
                      <option value="system">System</option>
                    </select>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Users className="w-5 h-5" />
                    <span>User Settings</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Timeline Filter Defaults</Label>
                      <p className="text-xs text-slate-500">
                        Configure which users should have timeline filter enabled by default
                      </p>
                    </div>
                    <Button
                      onClick={() => setShowUserManagementModal(true)}
                      variant="outline"
                      size="sm"
                    >
                      Manage Users
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="flex justify-end">
              <Button
                onClick={handleSaveSettings}
                disabled={settingsLoading}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
              >
                {settingsLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save General Settings
                  </>
                )}
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="security">
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Shield className="w-5 h-5" />
                    <span>Security Settings</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="defaultUserRole">Default User Role</Label>
                    <select
                      id="defaultUserRole"
                      value={settings.defaultUserRole}
                      onChange={(e) => handleSettingChange('defaultUserRole', e.target.value)}
                      className="w-full p-2 border border-slate-300 rounded-md text-sm"
                    >
                      <option value="read_only">Read Only</option>
                      <option value="contributor">Contributor</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Enable Audit Log</Label>
                      <p className="text-xs text-slate-500">
                        Track all user actions and changes
                      </p>
                    </div>
                    <Switch
                      checked={settings.enableAuditLog}
                      onCheckedChange={(checked) => handleSettingChange('enableAuditLog', checked)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="maxFileSize">Max File Size (MB)</Label>
                    <Input
                      id="maxFileSize"
                      type="number"
                      min="1"
                      max="100"
                      value={settings.maxFileSize}
                      onChange={(e) => handleSettingChange('maxFileSize', parseInt(e.target.value))}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="flex justify-end">
              <Button
                onClick={handleSaveSettings}
                disabled={settingsLoading}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
              >
                {settingsLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Security Settings
                  </>
                )}
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="system">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Database className="w-5 h-5" />
                <span>System Status</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Database</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-sm text-green-600">Connected</span>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">API Server</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-sm text-green-600">Running</span>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">File Storage</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <div className="text-sm text-green-600">
                      {diskSpace ? `${diskSpace.available_space} GB free` : 'Checking...'}
                    </div>
                  </div>
                </div>

                {diskSpace && (
                  <div className="ml-6 pl-4 border-l-2 border-slate-200">
                    <div className="text-xs text-slate-600 space-y-1">
                      <div>• Total: {diskSpace.total_space} GB</div>
                      <div>• Used: {diskSpace.used_space} GB</div>
                      <div>• Available: {diskSpace.available_space} GB ({diskSpace.percentage_used}% used)</div>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Email Service</span>
                  <div className="flex items-center space-x-2">
                    <div className={`w-2 h-2 rounded-full ${
                      emailConfig && emailConfig.configured
                        ? 'bg-green-500'
                        : 'bg-yellow-500'
                    }`}></div>
                    <span className={`text-sm ${
                      emailConfig && emailConfig.configured
                        ? 'text-green-600'
                        : 'text-yellow-600'
                    }`}>
                      {emailConfig && emailConfig.configured
                        ? 'Configured'
                        : 'Check Email Settings'
                      }
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="email">
          <EmailIntegrationSettings />
        </TabsContent>

        <TabsContent value="sharepoint">
          <SharePointIntegrationSettings />
        </TabsContent>

        <TabsContent value="products">
          <ProductPermissionsSettings />
        </TabsContent>

        <TabsContent value="messenger">
          <MessengerPermissionsSettings />
        </TabsContent>
      </Tabs>

      {/* User Management Modal */}
      <UserManagementModal
        isOpen={showUserManagementModal}
        onClose={() => setShowUserManagementModal(false)}
      />
    </div>
  );
};

export default AdminPanel;